import { useCallback, useEffect, useRef, MutableRefObject } from "react";
import type { NextRouter } from "next/router";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { Category } from "@lib/shared/types";
import { FieldData, Operation } from "../types";
import { DomainInfo } from "src/api/domainInfo";
import { DomainForm } from "src/api/domainForm";
import { buildThankYouUrl } from "../utils";

export interface UseThankYouRedirectArgs {
    formSubmitted: boolean;
    goToThankYouPage: boolean | undefined;
    skipToThankYou: boolean;
    otp: { isRequired: boolean };
    category: Category | null | undefined;
    isSecondServiceForm: boolean | undefined;
    dynamicThankYouFields: string[] | undefined;
    fieldsData: { [k: string]: FieldData };
    loadOneToOneForm: boolean | undefined;
    operation: Operation | null | undefined;
    router: NextRouter;
    isFormSubmitted: MutableRefObject<boolean>;
    // For building the thank-you URL internally
    domain: DomainInfo;
    locale: string;
    defaultLocale: string | undefined;
    form: DomainForm;
    ignoreCategoryInThankYouPagePath: boolean;
    onInlineThankYouShown?: () => void;
}

/**
 * Triggers the thank-you page redirect once the form has been submitted and
 * (for one-to-one flows) a winner buyer has been resolved. Behavior is
 * preserved verbatim from the previous inline implementation including the
 * existing dep array.
 */
export function useThankYouRedirect({
    formSubmitted,
    goToThankYouPage,
    skipToThankYou,
    otp,
    category,
    isSecondServiceForm,
    dynamicThankYouFields,
    fieldsData,
    loadOneToOneForm,
    operation,
    router,
    isFormSubmitted,
    domain,
    locale,
    defaultLocale,
    form,
    ignoreCategoryInThankYouPagePath,
    onInlineThankYouShown,
}: UseThankYouRedirectArgs): void {
    // The exit-modal form re-renders continuously (its `result` form object is
    // rebuilt on every render in FormOfferModal), so the effects below would
    // otherwise re-fire on every render once the form is submitted. The first
    // one would re-assign `window.location.href` on every render — the browser
    // keeps restarting the navigation and never settles, which surfaces as
    // "infinite redirections" when submitting from the exit modal. These refs
    // make the redirect / inline-thank-you handoff fire exactly once.
    const hasRedirectedRef = useRef(false);
    const hasShownInlineThankYouRef = useRef(false);

    const goToThankYou = useCallback(
        (
            queryString: string,
            params: Record<string, string>,
            leadUuid?: string,
        ) =>
            buildThankYouUrl({
                domain,
                category,
                locale,
                defaultLocale,
                operation,
                form,
                fieldsData,
                ignoreCategoryInThankYouPagePath,
                queryString,
                params,
                leadUuid,
            }),
        [domain, category, locale, fieldsData, operation],
    );

    useEffect(() => {
        const params: { [x: string]: string } = {};

        const hasNoSecondService =
            !category?.secondServiceCategories ||
            category.secondServiceCategories.length === 0 ||
            !!isSecondServiceForm;

        const shouldRedirectAfterSubmit =
            formSubmitted &&
            !!goToThankYouPage &&
            !otp.isRequired &&
            hasNoSecondService;

        const shouldRedirectOnSkip = skipToThankYou && !!goToThankYouPage;

        if (shouldRedirectAfterSubmit || shouldRedirectOnSkip) {
            const queryString = gettingQueriesAsString(router.query, "start");
            if (dynamicThankYouFields && !isSecondServiceForm) {
                dynamicThankYouFields.forEach((field) => {
                    params[field] = fieldsData[field]?.value;
                });
            }
            // For one-to-one consent flows, wait until a winner buyer is
            // resolved before redirecting; for the legacy (non-OTO) flow,
            // proceed to thank-you immediately as soon as the form is submitted.
            const isNonOtoFlow = !loadOneToOneForm;
            const winnerBuyerResolved =
                operation?.winnerBuyer !== undefined ||
                operation?.winnerBuyer !== null;
            if (
                (isNonOtoFlow || winnerBuyerResolved) &&
                !hasRedirectedRef.current
            ) {
                hasRedirectedRef.current = true;
                isFormSubmitted.current = true;
                (
                    window as unknown as Record<string, unknown>
                ).__anyFormSubmitted = true;
                const leadUuid = localStorage.getItem("mainId") ?? undefined;
                window.location.href = goToThankYou(
                    queryString,
                    params,
                    leadUuid,
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formSubmitted,
        router,
        goToThankYouPage,
        dynamicThankYouFields,
        skipToThankYou,
        category,
        operation,
        loadOneToOneForm,
    ]);

    // Call the callback when form is submitted with inline thank you (no redirect).
    // Guarded so it fires only once even though `onInlineThankYouShown` is a new
    // closure on every render (recreated inline in FormInOfferModal), which would
    // otherwise re-invoke the exit-modal close/submit handoff on every re-render.
    useEffect(() => {
        if (
            formSubmitted &&
            !goToThankYouPage &&
            onInlineThankYouShown &&
            !hasShownInlineThankYouRef.current
        ) {
            hasShownInlineThankYouRef.current = true;
            onInlineThankYouShown();
        }
    }, [formSubmitted, goToThankYouPage, onInlineThankYouShown]);
}
